import {noop} from '@benchling/js-utils';
import {Dispatch, SetStateAction} from 'react';

import {makeReactContext} from 'lib/makeReactContext';
import {ErrorBadgeError} from 'procedures/design/error-panel/ErrorBadge';
import {
  ProcedureRunTab,
  type ProcedureRunOrPlanStateSummary,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {ProcedureRunSelectionForAnalysis} from 'studies/typed/shared/analysis/useProcedureRunSelectionForAnalysis';

import {StudyDesignTabEditState} from '../design-tabs/StudyDesignTabSavingButtons';

/** Shared selection/tab state for the study overview. */
export interface StudyOverviewContextValue {
  selectedProcedureRun: ProcedureRunOrPlanStateSummary;
  selectedProcedureRunPlan: ProcedureRunOrPlanStateSummary;
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  promotingPlanIds: Set<string>;
  selectedProcedureRunsForAnalysis: Array<ProcedureRunSelectionForAnalysis>;
  addPromotingPlanId: (planId: string) => void;
  removePromotingPlanId: (planId: string) => void;
  setSelectedProcedureRunsForAnalysis: Dispatch<SetStateAction<Array<ProcedureRunSelectionForAnalysis>>>;
  isProcedureRunDescriptionInEditMode: boolean;
  setIsProcedureRunDescriptionInEditMode: Dispatch<SetStateAction<boolean>>;
  isProcedureRunDesignInEditMode: boolean;
  setIsProcedureRunDesignInEditMode: Dispatch<SetStateAction<boolean>>;
}

export const StudyOverviewContext = makeReactContext<StudyOverviewContextValue>(
  () => ({
    selectedProcedureRun: {id: null, name: null},
    selectedProcedureRunPlan: {id: null, name: null, procedureId: null},
    currentProcedureRunTab: ProcedureRunTab.OVERVIEW,
    setCurrentProcedureRunTab: noop,
    promotingPlanIds: new Set(),
    selectedProcedureRunsForAnalysis: [],
    addPromotingPlanId: noop,
    removePromotingPlanId: noop,
    setSelectedProcedureRunsForAnalysis: noop,
    isProcedureRunDescriptionInEditMode: false,
    setIsProcedureRunDescriptionInEditMode: noop,
    isProcedureRunDesignInEditMode: false,
    setIsProcedureRunDesignInEditMode: noop,
  }),
  'StudyOverviewContext'
);

export const useStudyOverviewContext = StudyOverviewContext.useContext;

/** Plan design state and handlers published for the overview header. */
export interface PlanDesignActions {
  editState: StudyDesignTabEditState;
  hasUnsavedChanges: boolean;
  onSave: () => Promise<void>;
  isSaveDisabled: boolean;
  saveDisabledTooltip?: string;
  errors: Array<ErrorBadgeError>;
}

/** Plan design actions the design tab publishes for the overview header to consume. */
export interface ProcedureRunPlanContextValue {
  planDesignActions: PlanDesignActions | null;
  setPlanDesignActions: (actions: PlanDesignActions | null) => void;
}

export const ProcedureRunPlanContext = makeReactContext<ProcedureRunPlanContextValue>(
  () => ({
    planDesignActions: null,
    setPlanDesignActions: noop,
  }),
  'ProcedureRunPlanContext'
);

export const useProcedureRunPlanContext = ProcedureRunPlanContext.useContext;

/** Run design edit state and handlers published for the overview header. */
export interface RunDesignActions {
  editState: StudyDesignTabEditState;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaveDisabled: boolean;
  saveDisabledTooltip?: string;
  isEditDisabled: boolean;
  editDisabledTooltip?: string;
}

/** Run design actions the design tab publishes for the overview header to consume. */
export interface ProcedureRunContextValue {
  runDesignActions: RunDesignActions | null;
  setRunDesignActions: (actions: RunDesignActions | null) => void;
}

export const ProcedureRunContext = makeReactContext<ProcedureRunContextValue>(
  () => ({
    runDesignActions: null,
    setRunDesignActions: noop,
  }),
  'ProcedureRunContext'
);

export const useProcedureRunContext = ProcedureRunContext.useContext;
